import React from 'react';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import config from '../config';

function HomeLegacy() {
  const siteName = process.env.REACT_APP_SITE_NAME || 'StepWiseDocs';

  return (
    <>
      <HeaderNavbar />

      <section className="gs-hero rounded-4 shadow-sm mb-5 mt-3">
        <div className="row align-items-center gy-4">
          <div className="col-lg-6">
            <span className="gs-badge text-uppercase">Open hardware, step by step</span>
            <h1 className="display-4 fw-bold text-white mb-3" style={{ maxWidth: 540 }}>
              {siteName} helps people build open hardware through clear, step-by-step guides.
            </h1>
            <p className="lead text-white-50 mb-4" style={{ maxWidth: 520 }}>
              Start following detailed build instructions right away or share your own guides with the community. Pick the path that fits your next build.
            </p>
            <div className="d-flex flex-wrap gap-3">
              <a href={`${config.prefixPath}/projects`} className="btn btn-primary btn-lg">
                Browse projects
              </a>
              <a href={`${config.prefixPath}/publish`} className="btn btn-outline-light btn-lg">
                Create your Project
              </a>
            </div>
            <div className="text-white-50 small mt-3">No account needed to explore public guides.</div>
          </div>
          <div className="col-lg-6">
            <div className="gs-hero-preview card border-0 shadow-lg">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="badge bg-primary-subtle text-primary-emphasis">Build guide preview</span>
                  <span className="text-muted small">Live WYSIWYG editor</span>
                </div>
                <div className="rounded-3 p-3 mb-3 d-flex justify-content-center">
                  <img
                    src="/images/editstep.gif"
                    alt="Placeholder: step preview"
                    className="img-fluid"
                    style={{ maxHeight: 255, objectFit: 'contain' }}
                  />
                </div>
                <ul className="gs-preview-list list-unstyled small mb-0 text-muted">
                  <li>
                    <span className="gs-preview-dot bg-primary-subtle" />
                    Each organization can have multiple projects, and each project can have multiple guides.
                  </li>
                  <li>
                    <span className="gs-preview-dot bg-primary-subtle" />
                    Multiple images per step, with visual annotations.
                  </li>
                  <li>
                    <span className="gs-preview-dot bg-primary-subtle" />
                    All you need to start building, in one place.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5 anchor-top" id="get-started">
        <div className="row mb-4">
          <div className="col-lg-8">
            <h2 className="h2 fw-semibold mb-2">Choose your starting point</h2>
            <p className="text-muted mb-0" style={{ maxWidth: 640 }}>
              StepWiseDocs serves both makers searching for their next build and teams ready to publish open hardware documentation.
            </p>
          </div>
        </div>
        <div className="row row-cols-1 row-cols-lg-2 g-4">
          <div className="col">
            <div className="card gs-feature-card h-100 border-0 shadow-sm">
              <div className="card-body d-flex flex-column">
                <div className="mb-2 text-primary fw-semibold">For builders and makers</div>
                <h3 className="h4">Follow expert build guides</h3>
                <p className="text-muted">
                  Discover curated open hardware projects with walkthroughs designed for real-world workbenches.
                </p>
                <ul className="mb-4" style={{ paddingLeft: '1.1rem' }}>
                  <li>Clear steps with photos and field notes</li>
                  <li>Multiple guide variations per project</li>
                  <li>Progress tracking so you can pause and resume</li>
                  <li>Responsive layouts built for mobile devices</li>
                </ul>
                <div className="mt-auto">
                  <a href={`${config.prefixPath}/projects`} className="btn btn-primary">
                    Browse projects
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card gs-feature-card h-100 border-0 shadow-sm">
              <div className="card-body d-flex flex-column">
                <div className="mb-2 text-primary fw-semibold">For open hardware project owners</div>
                <h3 className="h4">Publish guides with confidence</h3>
                <p className="text-muted">
                  Share the details your community needs using an editor built for hardware documentation.
                </p>
                <ul className="mb-4" style={{ paddingLeft: '1.1rem' }}>
                  <li>Create projects with multiple build guides</li>
                  <li>Visual, WYSIWYG step editor with live previews</li>
                  <li>Manage draft and published versions seamlessly</li>
                  <li>Upload images from mobile but edit the steps on desktop at the same time.</li>
                </ul>
                <div className="mt-auto">
                  <a href={`${config.prefixPath}/publish`} className="btn btn-outline-dark">
                    Create your Project
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5">
        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="card-body p-4 p-lg-5">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <h3 className="h4 fw-semibold mb-3">Why teams rely on {siteName}</h3>
                <ul className="mb-0" style={{ paddingLeft: '1.1rem' }}>
                  <li>Easy to build. Easy to follow.</li>
                  <li>Rich media support helps makers avoid guesswork.</li>
                  <li>Hosted workflow means no custom tooling or deployments to maintain.</li>
                </ul>
              </div>
              <div className="col-lg-5">
                <div className="bg-white rounded-3 border p-4 text-center h-100 d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <img
                      src="/images/step.png"
                      alt="Placeholder: documentation workflow"
                      className="img-fluid"
                      style={{ maxHeight: 245, objectFit: 'contain' }}
                    />
                  </div>
                  <p className="text-muted small mb-0">Bring every build step into one shared workspace.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default HomeLegacy;
